/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { useMemo } from 'react';
import {
  styled,
  Select,
  MenuItem,
  SelectProps,
  FormHelperText,
  FormControl as MuiFormControl,
  FormLabel as MuiFormLabel,
  FormLabelProps as MuiFormLabelProps,
  useTheme,
  SxProps,
  Theme,
} from '@mui/material';
import { FontWeightType, FormLabelPosition } from '../util/types';
import MaterialSymbol from './MaterialSymbol';
import { colors } from '@farmlands/mui-theme';

interface FormControlProps extends MuiFormLabelProps {
  labelposition?: FormLabelPosition;
}

interface FormLabelProps extends MuiFormLabelProps {
  labelposition?: FormLabelPosition;
  labelfontweight?: FontWeightType;
}

export interface DropdownItem {
  label?: string;
  value: string;
  disabled?: boolean;
}

type DropdownListProps = {
  items: DropdownItem[];
  helperText?: string;
  labelposition?: FormLabelPosition;
  labelfontweight?: FontWeightType;
  testId?: string;
  renderItem?: (value: string | number) => React.ReactNode;
  sxFormControl?: SxProps<Theme>;
} & SelectProps<string>;

const FormControl = styled(MuiFormControl)<FormControlProps>(({ labelposition }) => ({
  display: 'flex',
  flex: 1,
  flexDirection: labelposition === 'top' ? 'column' : 'row',
  alignItems: labelposition === 'top' ? 'flex-start' : 'center',
  justifyContent: 'center',
}));

const FormLabel = styled(MuiFormLabel)<FormLabelProps>(({ labelposition, labelfontweight }) => ({
  display: 'flex',
  minWidth: 'fit-content',
  marginBottom: labelposition === 'top' ? '4px' : '0',
  paddingRight: labelposition === 'top' ? '0' : '10px',
  fontWeight: labelfontweight,
}));

const DropdownList = ({
  items,
  label,
  labelposition = 'top',
  labelfontweight = 700,
  helperText,
  error,
  testId,
  value,
  disabled,
  onChange,
  renderItem,
  sxFormControl,
  sx,
  ...props
}: DropdownListProps) => {
  const theme = useTheme();
  const renderLabel = useMemo(() => {
    return (
      <FormLabel
        id={testId + '-label'}
        labelposition={labelposition}
        labelfontweight={labelfontweight}
      >
        {label}
      </FormLabel>
    );
  }, [labelposition, labelfontweight]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderIcon = (props: any) => (
    <div {...props}>
      <MaterialSymbol>expand_more</MaterialSymbol>
    </div>
  );
  return (
    <FormControl labelposition={labelposition} sx={sxFormControl}>
      {label && renderLabel}
      <Select
        id={testId}
        labelId={testId + '-label'}
        data-testid={testId}
        sx={{
          '&:hover fieldset': {
            border: `1px solid ${theme.colors.primary500} !important`,
          },
          '&.Mui-disabled': {
            backgroundColor: colors.grey100,
          },
          ...sx,
        }}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          ...props.MenuProps,
        }}
        IconComponent={renderIcon}
        value={value}
        onChange={onChange}
        error={error}
        disabled={disabled}
      >
        {renderItem
          ? items.map((item) => renderItem(item.value))
          : items.map((item) => (
              <MenuItem
                data-testid={`${testId}-item`}
                key={`${item.value}`}
                value={item.value}
                disabled={item.disabled}
              >
                {item.label ?? item.value}
              </MenuItem>
            ))}
      </Select>
      <FormHelperText error={error}>{helperText}</FormHelperText>
    </FormControl>
  );
};

export default DropdownList;
